import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import {
	validateFormDataAction,
	formKeySelector,
	IWithForms,
	FormBuilder,
} from 'formBuilder/src'
import { TranslationFile } from 'i18n'
import React, { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import updateApplicationServices from 'services/updateApplicationServices'
import { FormFactory } from 'formBuilder/formFactory'
import { CustomerForm } from 'constants/enums/formTypes'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { IApplicationContext } from 'interfaces/i-application-context-props'

const AdditionalDetailsUk = () => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
	])
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const dispatch = useDispatch()
	const store = useStore()
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()

	let applicationContactFormSchema = FormFactory(
		CustomerForm.ApplicationContactInformationForm,
		{
			formName: 'applicationContactForm',
			blockName: 'applicationContactForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
		// sanitizeApplicationData(
		// 	loanApplication.applicationData as IApplicationInformation,
		// ),
	)
	const applicationContactFormStateValues = useSelector(
		formKeySelector(
			applicationContactFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	let applicationFinancialFormSchema = FormFactory(
		CustomerForm.ApplicationFinancialForm,
		{
			formName: 'applicationFinancialForm',
			blockName: 'applicationFinancialForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
		// sanitizeApplicationData(
		// 	loanApplication.applicationData as IApplicationInformation,
		// ),
	)
	const applicationFinancialFormStateValues = useSelector(
		formKeySelector(
			applicationFinancialFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	let applicationNationalityFormSchema = FormFactory(
		CustomerForm.ApplicationNationalityForm,
		{
			formName: 'applicationNationalityForm',
			blockName: 'applicationNationalityForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
		// sanitizeApplicationData(
		// 	loanApplication.applicationData as IApplicationInformation,
		// ),
	)
	const applicationNationalityFormStateValues = useSelector(
		formKeySelector(
			applicationNationalityFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	let applicationCreditLimitFormSchema = FormFactory(
		CustomerForm.ApplicationCreditLimitForm,
		{
			formName: 'applicationCreditLimitForm',
			blockName: 'applicationCreditLimitForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
		// sanitizeApplicationData(
		// 	loanApplication.applicationData as IApplicationInformation,
		// ),
	)
	const applicationCreditLimitFormStateValues = useSelector(
		formKeySelector(
			applicationCreditLimitFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	let applicationMarketingPreferenceFormSchema = FormFactory(
		CustomerForm.ApplicationMarketingPreferenceForm,
		{
			formName: 'applicationMarketingPreferenceForm',
			blockName: 'applicationMarketingPreferenceForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)
	const applicationMarketingPreferenceFormStateValues = useSelector(
		formKeySelector(
			applicationMarketingPreferenceFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	// whenever any field is updated in the forms, dispatch the updateApplicationData on the loanApplication
	useEffect(() => {
		dispatchLoanApplication({
			type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
			payload: {
				...loanApplication,
				updateApplicationData: {
					...loanApplication.updateApplicationData,
					applicant: {
						...loanApplication.updateApplicationData?.applicant,
						contacts: {
							...loanApplication.updateApplicationData?.applicant
								?.contacts,
							cellPhone:
								applicationContactFormStateValues?.cellPhone
									.value,
							preferredContactType:
								applicationMarketingPreferenceFormStateValues
									?.marketingPreferences.value,
						},
						employment: {
							...loanApplication.updateApplicationData?.applicant
								?.employment,
							jobTitle:
								applicationContactFormStateValues?.jobTitle
									.value,
						},
						personalInfo: {
							...loanApplication.updateApplicationData?.applicant
								?.personalInfo,
							countryOfBirth:
								applicationNationalityFormStateValues
									?.countryOfBirth.value,
							citizenship: [
								applicationNationalityFormStateValues
									?.citizenship.value,
							],
						},
						address: {
							...loanApplication.updateApplicationData?.applicant
								?.address,
							countryOfResidence:
								applicationNationalityFormStateValues
									?.countryOfResidence.value,
						},
					},
					financialSituation:
						applicationFinancialFormStateValues?.financialSituation
							.value,

					creditLimitPreference:
						applicationCreditLimitFormStateValues
							?.creditLimitPreference.value,
				},
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		applicationContactFormStateValues,
		applicationFinancialFormStateValues,
		applicationNationalityFormStateValues,
		applicationCreditLimitFormStateValues,
		applicationMarketingPreferenceFormStateValues,
	])

	const continueBtnClick = async () => {
		await submitApplicationData()

		// dispatchMsp({
		// 	type: MSP_ACTIONS.SET_INDEX,
		// 	payload: {
		// 		index: msp.index + 1,
		// 	},
		// })
	}

	const backBtnClick = async () => {
		dispatchMsp({
			type: MSP_ACTIONS.SET_INDEX,
			payload: {
				index: msp.index - 1,
			},
		})
	}

	const submitApplicationData = async () => {
		dispatch(validateFormDataAction('applicationContactForm'))
		dispatch(validateFormDataAction('applicationFinancialForm'))
		dispatch(validateFormDataAction('applicationNationalityForm'))
		dispatch(validateFormDataAction('applicationCreditLimitForm'))
		dispatch(validateFormDataAction('applicationMarketingPreferenceForm'))

		const errors = formKeySelector(
			'applicationContactForm',
			'errors',
		)(store.getState() as IWithForms)
		if (errors?.length) {
			setTimeout(() => {
				document
					.querySelector<HTMLElement>('.has-error')
					?.scrollIntoView({ behavior: 'smooth', block: 'start' })
			}, 100)
			return
		}

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.updateApplicationUK(
					loanApplication.applicationData!,
					loanApplication.updateApplicationData!,
				)
				.then(async (response) => {
					if (response.status === 200) {
						dispatchMsp({
							type: MSP_ACTIONS.SET_INDEX,
							payload: {
								index: msp.index + 1,
							},
						})
					}
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	return (
		<div className="application-details-uk">
			<div className="multistep-content">
				<h2 className="multistep-header">
					{t('AdditionalDetailsHeader')}
				</h2>
			</div>
			<br />
			<div>
				<p>{t('AdditionalDetailsDescription')}</p>
			</div>

			<div className="update-personal-data">
				<div className="update-personal-data-section">
					{applicationContactFormSchema ? (
						<FormBuilder
							formSchema={applicationContactFormSchema}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={false}
						/>
					) : (
						<CircleSpinner />
					)}
				</div>
			</div>

			<div className="update-personal-data">
				<div className="update-personal-data-section">
					{applicationFinancialFormSchema ? (
						<FormBuilder
							formSchema={applicationFinancialFormSchema}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={false}
						/>
					) : (
						<CircleSpinner />
					)}
				</div>
			</div>

			<div className="update-personal-data">
				<div className="update-personal-data-section">
					{applicationNationalityFormSchema ? (
						<FormBuilder
							formSchema={applicationNationalityFormSchema}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={false}
						/>
					) : (
						<CircleSpinner />
					)}
				</div>
			</div>

			<div className="update-personal-data">
				<div className="update-personal-data-section">
					{applicationCreditLimitFormSchema ? (
						<FormBuilder
							formSchema={applicationCreditLimitFormSchema}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={false}
						/>
					) : (
						<CircleSpinner />
					)}
				</div>
			</div>

			<div className="update-personal-data">
				<div className="update-personal-data-section">
					{applicationMarketingPreferenceFormSchema ? (
						<FormBuilder
							formSchema={
								applicationMarketingPreferenceFormSchema
							}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={false}
						/>
					) : (
						<CircleSpinner />
					)}
				</div>
			</div>

			<p className="marketing-privacy-policy">
				<Trans
					i18nKey="MarketingPrivacyPolicy"
					components={{
						1: (
							<a
								href="https://google.com"
								target="_blank"
								rel="noreferrer"
							/>
						),
					}}
				/>
			</p>

			<div className="step-actions">
				<button
					type="button"
					disabled={loading}
					className="btn btn-secondary"
					onClick={() => {
						setTimeout(() => {
							backBtnClick()
						}, 400)
					}}>
					{t('Back')}
				</button>
				&nbsp;
				<button
					type="button"
					disabled={loading}
					className="btn btn-primary"
					onClick={() => {
						setTimeout(() => {
							continueBtnClick()
						}, 400)
					}}>
					{t('Continue')}
				</button>
			</div>
		</div>
	)
}

export default AdditionalDetailsUk
